import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { API_URL } from '../../config/apiConfig';
import {
  FaEye,
  FaReply,
  FaTimes,
  FaCheckCircle,
  FaHourglass,
  FaCircle,
} from 'react-icons/fa';

const SupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState('in-progress');
  const [isReplying, setIsReplying] = useState(false);
  const { user: adminInfo } = useSelector((state) => state.auth);

  const limit = 10;

  useEffect(() => {
    fetchMessages();
  }, [filter, page]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          Authorization: `Bearer ${adminInfo?.token}`,
        },
      };

      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const response = await axios.get(
        `${API_URL}/support-message/admin/all?page=${page}&limit=${limit}${statusParam}`,
        config
      );

      if (response.data.success) {
        setMessages(response.data.data);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching support messages:', error);
      toast.error('Failed to load support messages');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (message) => {
    setSelectedMessage(message);
    setShowDetailModal(true);
  };

  const handleReplyClick = (message) => {
    setSelectedMessage(message);
    setReplyText('');
    setReplyStatus('in-progress');
    setShowReplyModal(true);
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsReplying(true);
      const config = {
        headers: {
          Authorization: `Bearer ${adminInfo?.token}`,
        },
      };

      const response = await axios.put(
        `${API_URL}/support-message/${selectedMessage._id}/reply`,
        {
          adminReply: replyText.trim(),
          status: replyStatus,
        },
        config
      );

      if (response.data.success) {
        toast.success('Reply sent successfully');
        setShowReplyModal(false);
        setReplyText('');
        fetchMessages();
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setIsReplying(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'text-red-600';
      case 'in-progress':
        return 'text-yellow-600';
      case 'resolved':
        return 'text-green-600';
      case 'closed':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <FaCircle className={`${getStatusColor(status)}`} />;
      case 'in-progress':
        return <FaHourglass className={`${getStatusColor(status)}`} />;
      case 'resolved':
        return <FaCheckCircle className={`${getStatusColor(status)}`} />;
      case 'closed':
        return <FaTimes className={`${getStatusColor(status)}`} />;
      default:
        return <FaCircle className="text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Support Messages</h1>
          <p className="text-gray-600 mt-2">Manage customer support tickets and respond to inquiries</p>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-2">
          {['all', 'open', 'in-progress', 'resolved', 'closed'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilter(status);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
            </button>
          ))}
        </div>

        {/* Messages Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 text-lg">No support messages found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Booking ID
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {messages.map((message) => (
                    <tr
                      key={message._id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-6 py-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-900">
                            {message.userId?.name || 'Unknown User'}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {message.userId?.phone}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {message.bookingNumber || message.bookingId}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <p className="font-medium text-gray-900 truncate">
                          {message.title}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(message.status)}
                          <span
                            className={`text-sm font-medium ${getStatusColor(
                              message.status
                            )}`}
                          >
                            {message.status.charAt(0).toUpperCase() +
                              message.status.slice(1).replace('-', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(message.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewDetails(message)}
                            className="text-blue-600 hover:text-blue-800 transition flex items-center gap-1"
                            title="View details"
                          >
                            <FaEye size={16} />
                          </button>
                          <button
                            onClick={() => handleReplyClick(message)}
                            className="text-green-600 hover:text-green-800 transition flex items-center gap-1"
                            title="Reply"
                          >
                            <FaReply size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition"
              >
                Next
              </button>
            </div>
          )}
        </div>

      {/* Detail Modal */}
      {showDetailModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-gray-900">
                Support Ticket Details
              </h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Customer Name</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedMessage.userId?.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedMessage.userId?.phone}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedMessage.userId?.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Booking ID</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedMessage.bookingNumber}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusIcon(selectedMessage.status)}
                  <span
                    className={`text-lg font-semibold ${getStatusColor(
                      selectedMessage.status
                    )}`}
                  >
                    {selectedMessage.status.charAt(0).toUpperCase() +
                      selectedMessage.status.slice(1).replace('-', ' ')}
                  </span>
                </div>
              </div>

              {/* Title */}
              <div>
                <p className="text-sm text-gray-500">Title</p>
                <p className="text-lg font-semibold text-gray-900 mt-2">
                  {selectedMessage.title}
                </p>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="text-gray-700 mt-2 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {selectedMessage.description}
                </p>
              </div>

              {/* Admin Reply */}
              {selectedMessage.adminReply && (
                <div className="border-t border-gray-200 pt-6">
                  <p className="text-sm text-gray-500">Admin Reply</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedMessage.repliedByAdmin?.name} replied on{' '}
                      {formatDate(selectedMessage.adminRepliedAt)}
                    </p>
                    <p className="text-gray-700 mt-2 whitespace-pre-wrap">
                      {selectedMessage.adminReply}
                    </p>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="border-t border-gray-200 pt-4 text-sm text-gray-500">
                <p>Submitted: {formatDate(selectedMessage.createdAt)}</p>
                {selectedMessage.updatedAt && (
                  <p>Last Updated: {formatDate(selectedMessage.updatedAt)}</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  handleReplyClick(selectedMessage);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition"
              >
                Reply to Ticket
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Reply to Ticket</h2>
              <button
                onClick={() => setShowReplyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-semibold text-gray-900">
                  {selectedMessage.title}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  From: {selectedMessage.userId?.name}
                </p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Update Status
                </label>
                <select
                  value={replyStatus}
                  onChange={(e) => setReplyStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Reply Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Reply <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your response to the customer..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                  disabled={isReplying}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowReplyModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition disabled:opacity-50"
                disabled={isReplying}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReply}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50"
                disabled={isReplying}
              >
                {isReplying ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportMessages;
